import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BRANDING_ASSET_MAX_BYTES,
  BRANDING_ASSET_SLOTS,
  BRANDING_COLOR_KEYS,
  parseColor,
  type BrandingAssetSlot,
  type BrandingColorKey,
  type BrandingSettings,
  type BrandingSettingsInput,
  type TenantBranding,
} from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { Button, ErrorNote, Field, inputClass, useCurrentUser } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { DEFAULT_NAME, getSavedBranding, setPreviewBranding, setSavedBranding } from "@/lib/branding";

/*
 * IMPOSTAZIONI → BRANDING: nome visualizzato, logo chiaro e scuro, favicon,
 * colori. Solo ADMIN, e il backend lo rifiuta comunque agli altri.
 *
 * **L'anteprima e' l'app stessa**: la bozza si applica alle variabili CSS, alla
 * barra laterale e al titolo della scheda mentre la si scrive
 * (`setPreviewBranding`). «Annulla», o uscire dalla pagina senza salvare,
 * rimette il marchio salvato.
 *
 * Qui i colori si controllano con la stessa grammatica del backend
 * (`parseColor`) solo per dirlo subito accanto al campo: il rifiuto vero e'
 * quello del backend, e nel CSS entrano comunque solo tre numeri.
 */

/** I gruppi della pagina, e quale variabile di `index.css` tocca ogni campo. */
export const GROUPS: { title: string; hint: string; fields: { key: BrandingColorKey; label: string }[] }[] = [
  { title: "Brand", hint: "Pulsanti principali, voce di menu attiva, bordi in evidenza", fields: [{ key: "accent", label: "Colore" }, { key: "on-accent", label: "Testo" }] },
  { title: "Pagina", hint: "Lo sfondo di ogni schermata", fields: [{ key: "page", label: "Sfondo" }] },
  { title: "Card e pannelli", hint: "Tabelle, schede, finestre", fields: [{ key: "surface", label: "Sfondo" }] },
  { title: "Evidenza", hint: "Campi, righe al passaggio del mouse, voce di menu attiva", fields: [{ key: "raised", label: "Sfondo" }] },
  { title: "Pulsanti secondari", hint: "Le azioni che non sono la principale", fields: [{ key: "secondary", label: "Sfondo" }] },
  { title: "Testo attenuato", hint: "Etichette, sottotitoli, spiegazioni", fields: [{ key: "muted", label: "Colore" }] },
  { title: "Bordi", hint: "Linee di tabelle, schede e campi", fields: [{ key: "line", label: "Colore" }] },
  { title: "Barra laterale", hint: "Lo sfondo del menu e il nome accanto al logo", fields: [{ key: "sidebar", label: "Sfondo" }, { key: "strong", label: "Nome" }] },
];

export const ASSET_LABEL: Record<BrandingAssetSlot, { title: string; hint: string }> = {
  logoDark: { title: "Logo scuro", hint: "Il logo su sfondo scuro: barra laterale e login." },
  logoLight: { title: "Logo chiaro", hint: "Usato quando ci sarà il tema chiaro. Per ora si vede solo nell'anteprima." },
  favicon: { title: "Favicon", hint: "L'icona della scheda del browser." },
};

export const ACCEPT = "image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico";

export type AssetDraft =
  | { kind: "keep" }
  | { kind: "remove" }
  | { kind: "new"; base64: string; objectUrl: string; name: string; size: number };

export type Colors = Record<BrandingColorKey, string>;

export const emptyColors = (): Colors => Object.fromEntries(BRANDING_COLOR_KEYS.map((k) => [k, ""])) as Colors;
export const keepAll = (): Record<BrandingAssetSlot, AssetDraft> => ({ logoLight: { kind: "keep" }, logoDark: { kind: "keep" }, favicon: { kind: "keep" } });
export const kb = (n: number) => `${Math.ceil(n / 1024)} KB`;

export function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ""));
    r.onerror = () => reject(new Error("Il file non si è potuto leggere"));
    r.readAsDataURL(file);
  });
}

/** Il colore che si vede adesso per quella variabile, in #rrggbb: serve al selettore quando il campo e' vuoto. */
function effectiveHex(key: BrandingColorKey): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${key}`).trim().split(/\s+/).map(Number);
  if (v.length !== 3 || v.some((n) => !Number.isFinite(n))) return "#000000";
  return `#${v.map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

export function Branding() {
  const user = useCurrentUser();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["branding-settings"], queryFn: api.brandingSettings.get, enabled: user.role === "ADMIN" });

  const [displayName, setDisplayName] = useState("");
  const [colors, setColors] = useState<Colors>(emptyColors);
  const [assets, setAssets] = useState<Record<BrandingAssetSlot, AssetDraft>>(keepAll);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = (d: BrandingSettings) => {
    setDisplayName(d.displayName ?? "");
    setColors({ ...emptyColors(), ...d.colors });
    setAssets((cur) => {
      for (const a of Object.values(cur)) if (a.kind === "new") URL.revokeObjectURL(a.objectUrl);
      return keepAll();
    });
  };

  useEffect(() => {
    if (q.data) reset(q.data);
  }, [q.data]);

  // Gli URL delle immagini scelte si liberano uscendo dalla pagina.
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  useEffect(
    () => () => {
      for (const a of Object.values(assetsRef.current)) if (a.kind === "new") URL.revokeObjectURL(a.objectUrl);
    },
    [],
  );

  const d = q.data;
  const parsed = useMemo(
    () => Object.fromEntries(BRANDING_COLOR_KEYS.map((k) => [k, colors[k].trim() ? parseColor(colors[k]) : undefined])) as Record<BrandingColorKey, ReturnType<typeof parseColor> | undefined>,
    [colors],
  );
  const invalid = BRANDING_COLOR_KEYS.filter((k) => parsed[k] === null);

  const assetUrl = (slot: BrandingAssetSlot): string | null => {
    const a = assets[slot];
    if (a.kind === "new") return a.objectUrl;
    if (a.kind === "remove") return null;
    return d?.assets[slot]?.url ?? null;
  };

  const dirty =
    !!d &&
    (displayName.trim() !== (d.displayName ?? "") ||
      BRANDING_COLOR_KEYS.some((k) => colors[k].trim() !== (d.colors[k] ?? "")) ||
      BRANDING_ASSET_SLOTS.some((s) => assets[s].kind !== "keep"));

  /*
   * L'ANTEPRIMA DAL VIVO. Solo i colori validi: uno scritto a meta' non
   * spegne quelli che sono gia' giusti. Senza modifiche non c'e' bozza, e si
   * vede il salvato.
   */
  useEffect(() => {
    if (!d || !dirty) {
      setPreviewBranding(null);
      return;
    }
    const base = getSavedBranding();
    const tokens: TenantBranding["tokens"] = {};
    for (const k of BRANDING_COLOR_KEYS) if (parsed[k]) tokens[k] = { dark: colors[k].trim() };
    setPreviewBranding({
      name: displayName.trim() || d.tenantName,
      logoLightUrl: assetUrl("logoLight"),
      logoDarkUrl: assetUrl("logoDark"),
      faviconUrl: assetUrl("favicon"),
      reportingCurrency: base?.reportingCurrency ?? "EUR",
      defaultLanguage: base?.defaultLanguage ?? "it",
      tokens,
    });
  }, [d, dirty, displayName, colors, assets, parsed]);

  // Uscendo dalla pagina senza salvare torna il marchio salvato.
  useEffect(() => () => setPreviewBranding(null), []);

  if (user.role !== "ADMIN") {
    return (
      <>
        <PageHeader title="Branding" />
        <p className="px-6 py-6 text-sm text-muted">Il marchio del tenant lo configura un amministratore.</p>
      </>
    );
  }

  const pick = async (slot: BrandingAssetSlot, file: File | undefined) => {
    setError(null);
    setNote(null);
    if (!file) return;
    // Un primo filtro per dirlo subito; il controllo vero (i primi byte) e' del backend.
    if (/svg/i.test(file.type) || /\.svg$/i.test(file.name)) {
      setError(`${ASSET_LABEL[slot].title}: SVG non accettato. Usa PNG, JPG, WebP o ICO.`);
      return;
    }
    const max = BRANDING_ASSET_MAX_BYTES[slot];
    if (file.size > max) {
      setError(`${ASSET_LABEL[slot].title}: ${kb(file.size)}, il massimo è ${kb(max)}.`);
      return;
    }
    try {
      const base64 = await readBase64(file);
      setAssets((cur) => {
        const prev = cur[slot];
        if (prev.kind === "new") URL.revokeObjectURL(prev.objectUrl);
        return { ...cur, [slot]: { kind: "new", base64, objectUrl: URL.createObjectURL(file), name: file.name, size: file.size } };
      });
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const removeAsset = (slot: BrandingAssetSlot) =>
    setAssets((cur) => {
      const prev = cur[slot];
      if (prev.kind === "new") URL.revokeObjectURL(prev.objectUrl);
      // Togliere un'immagine appena scelta e mai salvata torna a quella salvata.
      return { ...cur, [slot]: prev.kind === "new" ? { kind: "keep" } : { kind: "remove" } };
    });

  const save = async () => {
    setError(null);
    setNote(null);
    setBusy(true);
    try {
      const body: BrandingSettingsInput = {
        displayName: displayName.trim() || null,
        colors: Object.fromEntries(BRANDING_COLOR_KEYS.map((k) => [k, colors[k].trim() || null])),
        assets: {},
      };
      for (const slot of BRANDING_ASSET_SLOTS) {
        const a = assets[slot];
        if (a.kind === "new") body.assets![slot] = { base64: a.base64 };
        if (a.kind === "remove") body.assets![slot] = null;
      }
      const saved = await api.brandingSettings.save(body);
      qc.setQueryData(["branding-settings"], saved);
      // Il marchio salvato si rilegge dalla rotta di tutti: e' quello che vedra' chi entra.
      setSavedBranding(await api.branding().catch(() => getSavedBranding()));
      setPreviewBranding(null);
      setNote("Salvato.");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setError(null);
    setNote(null);
    if (d) reset(d);
  };

  return (
    <>
      <PageHeader title="Branding" subtitle="Nome, logo, icona e colori del gestionale, del portale cliente e della pagina di accesso" />
      <div className="grid gap-6 px-6 py-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="max-w-2xl space-y-6">
          {q.error && <ErrorNote>{errorMessage(q.error)}</ErrorNote>}

          <Field label="Nome visualizzato" hint={`Nella barra laterale e nel titolo della scheda. Vuoto = «${d?.tenantName ?? DEFAULT_NAME}».`}>
            {(id) => <input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputClass} maxLength={60} placeholder={d?.tenantName} />}
          </Field>

          <section className="space-y-4">
            <h2 className="text-sm font-medium text-ink">Immagini</h2>
            <p className="-mt-2 text-xs text-faint">PNG, JPG, WebP o ICO; niente SVG. Logo fino a 500 KB, favicon fino a 100 KB.</p>
            {(["logoDark", "logoLight", "favicon"] as const).map((slot) => (
              <AssetField key={slot} slot={slot} url={assetUrl(slot)} draft={assets[slot]} saved={d?.assets[slot] ?? null} onPick={(f) => void pick(slot, f)} onRemove={() => removeAsset(slot)} />
            ))}
          </section>

          <section className="space-y-4">
            <h2 className="text-sm font-medium text-ink">Colori</h2>
            <p className="-mt-2 text-xs text-faint">
              Esadecimale (#c9a13a), rgb(), hsl() o oklch(). Vuoto = tema predefinito. Il gestionale ha solo il tema scuro: un valore per colore.
            </p>
            {GROUPS.map((g) => (
              <fieldset key={g.title} className="rounded-card border border-line bg-surface p-3">
                <legend className="px-1 text-sm text-ink">{g.title}</legend>
                <p className="mb-2 text-xs text-faint">{g.hint}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {g.fields.map((f) => (
                    <ColorField
                      key={f.key}
                      name={f.key}
                      label={f.label}
                      value={colors[f.key]}
                      parsed={parsed[f.key]}
                      onChange={(v) => setColors((c) => ({ ...c, [f.key]: v }))}
                    />
                  ))}
                </div>
              </fieldset>
            ))}
          </section>

          {invalid.length > 0 && <ErrorNote>Colori non validi: correggili o svuotali prima di salvare.</ErrorNote>}
          {error && <ErrorNote>{error}</ErrorNote>}
          {note && <p className="text-sm text-muted" role="status">{note}</p>}
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => void save()} disabled={busy || !d || !dirty || invalid.length > 0}>
              Salva
            </Button>
            <Button onClick={cancel} disabled={busy || !dirty}>
              Annulla
            </Button>
            {dirty && <span className="self-center text-xs text-faint">Anteprima dal vivo: niente è salvato finché non premi Salva.</span>}
          </div>
        </div>

        <Preview name={displayName.trim() || d?.tenantName || DEFAULT_NAME} logoDark={assetUrl("logoDark")} logoLight={assetUrl("logoLight")} />
      </div>
    </>
  );
}

export function AssetField({
  slot,
  url,
  draft,
  saved,
  onPick,
  onRemove,
}: {
  slot: BrandingAssetSlot;
  url: string | null;
  draft: AssetDraft;
  saved: BrandingSettings["assets"][BrandingAssetSlot];
  onPick: (f: File | undefined) => void;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const label = ASSET_LABEL[slot];
  const stato =
    draft.kind === "new"
      ? `${draft.name} · ${kb(draft.size)} · da salvare`
      : draft.kind === "remove"
        ? "Sarà tolto al salvataggio"
        : saved
          ? saved.size !== null
            ? `${saved.mime} · ${kb(saved.size)}`
            : "Impostato da un preset"
          : "Nessuna immagine";
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded border border-line ${slot === "logoLight" ? "bg-white" : "bg-page"}`}>
        {url ? <img src={url} alt="" className="max-h-10 max-w-10 object-contain" /> : <span className="text-2xs text-faint">—</span>}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink">{label.title}</p>
        <p className="text-xs text-faint">{label.hint}</p>
        <p className="text-xs text-muted">{stato}</p>
      </div>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <Button size="sm" onClick={() => input.current?.click()}>
        Scegli…
      </Button>
      {(url || draft.kind === "new") && (
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Togli
        </Button>
      )}
    </div>
  );
}

export function ColorField({
  name,
  label,
  value,
  parsed,
  onChange,
}: {
  name: BrandingColorKey;
  label: string;
  value: string;
  parsed: ReturnType<typeof parseColor> | undefined;
  onChange: (v: string) => void;
}) {
  const hex = parsed ? `#${parsed.rgb.map((n) => n.toString(16).padStart(2, "0")).join("")}` : effectiveHex(name);
  return (
    <Field
      label={label}
      hint={
        parsed === null ? (
          <span className="text-loss">Non valido: usa #rrggbb, rgb(), hsl() o oklch().</span>
        ) : parsed?.clipped ? (
          <span className="text-warn">Fuori dalla gamma sRGB: si mostra il colore più vicino.</span>
        ) : !value.trim() ? (
          "Predefinito"
        ) : undefined
      }
    >
      {(id) => (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={hex}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-9 shrink-0 cursor-pointer rounded border border-line bg-transparent p-0.5"
            aria-label={`${label}: selettore`}
          />
          <input
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} font-mono ${parsed === null ? "border-loss" : ""}`}
            placeholder="predefinito"
            spellCheck={false}
            maxLength={64}
          />
          {value && (
            <button type="button" onClick={() => onChange("")} className="shrink-0 text-xs text-muted hover:text-ink">
              Svuota
            </button>
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * Un pezzo d'app in miniatura, coi token veri: siccome la bozza e' applicata
 * alle variabili, questo riquadro e il resto dello schermo dicono la stessa
 * cosa. Il logo chiaro sta qui e basta, finche' non c'e' un tema chiaro.
 */
function Preview({ name, logoDark, logoLight }: { name: string; logoDark: string | null; logoLight: string | null }) {
  return (
    <aside className="space-y-3 lg:sticky lg:top-5 lg:self-start">
      <p className="text-xs text-faint">Anteprima</p>
      <div className="overflow-hidden rounded-card border border-line bg-page">
        <div className="flex">
          <div className="w-24 shrink-0 space-y-1 border-r border-line bg-sidebar p-2">
            <div className="flex items-center gap-1">
              {logoDark && <img src={logoDark} alt="" className="h-4 w-4 object-contain" />}
              <span className="truncate text-2xs font-semibold text-strong">{name}</span>
            </div>
            <div className="rounded border-l-2 border-accent bg-raised px-1 py-0.5 text-2xs text-accent">Attiva</div>
            <div className="px-1 py-0.5 text-2xs text-muted">Voce</div>
          </div>
          <div className="min-w-0 flex-1 space-y-2 p-2">
            <div className="rounded-card border border-line bg-surface p-2">
              <p className="text-xs text-ink">Card</p>
              <p className="text-2xs text-muted">Testo attenuato</p>
            </div>
            <div className="flex gap-1">
              <span className="rounded bg-accent px-2 py-1 text-2xs font-medium text-on-accent">Principale</span>
              <span className="rounded border border-line bg-secondary px-2 py-1 text-2xs text-ink">Secondario</span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs text-faint">Logo chiaro · usato quando ci sarà il tema chiaro</p>
        <div className="mt-1 flex h-14 items-center justify-center rounded-card border border-line bg-white">
          {logoLight ? <img src={logoLight} alt="" className="max-h-10 object-contain" /> : <span className="text-2xs text-neutral-400">Nessun logo chiaro</span>}
        </div>
      </div>
    </aside>
  );
}
